import {
    InsightEarningRelease,
    InsightGetSymbolFundamentalsAnnualResponse,
    InsightGetSymbolsPerTypeResponse,
    InsightIPOResponse,
    InsightLatestAnalystRatingsOnStockResponse,
} from '@orbis-systems/orbis-api-clients/insight';
import { fromZonedTime } from 'date-fns-tz';
import { compact, get, isArray, isEqual, sortBy, sumBy, uniqBy, uniqWith } from 'lodash-es';
import { MARKET_TIMEZONE } from '../constants/datePatterns';
import {
    AccountClassification,
    AccountHistoryStatus,
    AccountType,
    ActivityType,
    CorporateActionsType,
    Etf,
    EtfCommonParam,
    Fundamentals,
    GlobalGroupCode,
    HistoryCashSubType,
    MarketCapClassification,
    OrderActualTransaction,
    OverExposedType,
    PositionType,
    SecurityType,
} from '../constants/lookups';
import {
    DriftReportItem,
    EtfBreakdownWithExtraInfo,
    EtfFilterOption,
    EtfInit,
    EtfInitCommonParams,
    HoldingInfo,
    IpoWithExtraInfo,
    RecommendationStat,
    SectorBreakDown,
    SectorRanking,
    TiprankOverviewWithExtraInfo,
} from '../types';
import {
    Account,
    AccountHistory,
    AccountHistoryWithExtraInfo,
    AccountWithExtraInfo,
    OverExposedAccount,
    OverExposedEntry,
    PositionAccount,
    PositionAccountWithExtraInfo,
} from '../types/Account.type';
import { CorporateAction, CorporateActionWithExtraInfo } from '../types/CorporateAction.type';
import {
    DocumentItem,
    DocumentItemWithExtraInfo,
    LegalDocument,
    LegalDocumentWithExtraInfo,
} from '../types/Document.type';
import { Model, ModelWithExtraInfo } from '../types/Model.type';
import { Order, OrderWithExtraInfo } from '../types/Order.type';
import { Portfolio, PortfolioWithExtraInfo, Position, PositionWithExtraInfo } from '../types/Portfolio.type';
import { MarketCapBreakdownStat } from '../types/Stat.type';

import { AccountDriftReportItem, ModelDriftReportItem } from './../types/DriftReport.type';

import {
    OptionChain,
    Quote,
    QuoteFundamentals,
    QuoteFundamentalsCategory,
    QuoteFundamentalsData,
} from './../types/Quote.type';
import { divide, multiply, subtract, sum } from './numberUtils';
import { getInverseType, getLeverageType, getSectorByIndustryCode, titleCase } from './stringUtils';

export function formatQuote(quote: Quote): Quote {
    return {
        ...quote,
        companyName: (quote.option ? quote.underlying?.companyName : quote.companyName) || '',
        lastPrice: quote.mutualFund ? quote.navPrice : quote.lastPrice,
        change: quote.mutualFund ? quote.navChange : quote.change,
        changePercent: quote.mutualFund ? divide(quote.navChange, quote.navYesterdayPrice) : quote.changePercent,
    };
}

export function formatOrders({
    orders,
    models,
    accounts,
    inventoryAccount,
    combineOptionLegs = true,
}: {
    orders: Order[];
    models: Model[];
    accounts: Account[];
    inventoryAccount?: Account;
    combineOptionLegs?: boolean;
}): OrderWithExtraInfo[] {
    let finalOrders = uniqBy(orders, (order) => order.ourRef).filter(
        (order) => !(order.accountId === inventoryAccount?.accountId && order.allocation),
    );

    if (combineOptionLegs) {
        finalOrders = finalOrders.flatMap((order) => (order.orderLegs?.length ? order.orderLegs : [order]));
    }

    return finalOrders.map((order) => {
        const quote = order.quote ? formatQuote(order.quote) : undefined;
        const model = models.find((model) => model.id === order.modelId);
        const account =
            order.accountId === inventoryAccount?.accountId
                ? inventoryAccount
                : accounts.find((account) => account.accountId === order.accountId);
        const isSellOrder =
            order.actualTransaction === OrderActualTransaction.SellToClose ||
            order.actualTransaction === OrderActualTransaction.BuyToClose ||
            order.actualTransaction === OrderActualTransaction.Sell ||
            order.actualTransaction === OrderActualTransaction.BuyToCover;

        const value = quote ? multiply(quote.lastPrice, order.execQty) : 0;

        const data = {
            ...order,
            gain: isSellOrder ? order.profitLoss : order.gain,
            companyName: quote?.companyName || '',
            model: model?.title || 'Average Price Account',
            fullName: account?.fullName || '',
            accountNumber: account?.accountNumber || '',
            costValue: multiply(order.execPx, order.execQty) || 0,
            value: quote?.option ? multiply(value, 100) : value,
            securityType: order.option
                ? order.noLegs > 1
                    ? 'Spread'
                    : 'Option'
                : order.mutualFund
                  ? 'Mutual Fund'
                  : order.bond
                    ? 'Bond'
                    : 'Equity',
            tradeType: order.modelId
                ? 'Block Trade'
                : order.allocation
                  ? 'Allocation'
                  : order.accountId === inventoryAccount?.accountId
                    ? 'Average Price Account'
                    : 'Single Account',
            total: multiply(order.expectedPx, order.quantity),
            execPx: order.execPx,
        };

        return data;
    });
}

export function formatPortfolioPositions(
    positions: Position[],
    accounts: AccountWithExtraInfo[],
    inventoryAccounts: Account[],
    totalBalance: number,
    symbol?: string,
    positionType?: PositionType,
    groupedSymbolsByFundType?: InsightGetSymbolsPerTypeResponse,
): PositionWithExtraInfo[] {
    return positions
        .filter(
            (position) =>
                (!symbol || position.quote.underlyingSymbol === symbol || position.symbol === symbol) &&
                (!positionType || position.type === positionType),
        )
        .map((position) => {
            const account = accounts.find((account) => account.accountId === position.accountId);
            const quote = formatQuote(position.quote);
            const inventoryAccount = inventoryAccounts.find((account) => account.accountId === position.accountId);
            const fxRate = position.fx?.lastPrice || 1;

            const finalAccount = account || inventoryAccount;
            const accountBalance = account?.balance || 0;

            return {
                ...position,
                fullName: finalAccount?.fullName || '',
                accountNumber: finalAccount?.accountNumber || '',
                accountType: finalAccount?.accountType || position.accountType,
                lastPrice: quote.lastPrice,
                // Dividing by zero results in Infinity or NaN
                // positive weight for all positions
                // Added currency conversion for total balance (fx rate we receive as position.fx.lastPrice)
                weight: totalBalance === 0 ? 0 : divide(Math.abs(position.value), Math.abs(totalBalance * fxRate)),
                accountWeight: accountBalance === 0 ? 0 : divide(Math.abs(position.value), Math.abs(accountBalance)),
                companyName: quote.companyName,
                marketCapClassification: quote.marketCapClassification || MarketCapClassification.Nano,
                dividendAmount: quote.dividendAmount || 0,
                dividendYield: quote.dividendYield || 0,
                dividendExDate: quote.dividendExDate || '',
                dividendPaymentDate: quote.dividendPaymentDate || '',
                inventory: finalAccount?.accountClassification === AccountClassification.Inventory || false,
                quote: {
                    ...quote,
                    // Mutual fund from quote is not reliable when using `portfolio/full` endpoint
                    mutualFund: groupedSymbolsByFundType?.MF.includes(position.symbol) || quote.mutualFund,
                },
                securityType: quote.option
                    ? SecurityType.Option
                    : quote.bond
                      ? SecurityType.Bond
                      : groupedSymbolsByFundType?.MF.includes(position.symbol)
                        ? SecurityType.MutualFund
                        : groupedSymbolsByFundType?.MF.includes(position.symbol)
                          ? SecurityType.Etf
                          : SecurityType.Equity,
            };
        });
}

export function formatPortfolio(
    portfolio: Portfolio,
    accounts: AccountWithExtraInfo[],
    inventoryAccounts: Account[],
    balance: number,
    {
        symbol = undefined,
        positionType = undefined,
        tipranks = undefined,
        groupedSymbolsByFundType = undefined,
    }: {
        symbol?: string;
        positionType?: PositionType;
        tipranks?: TiprankOverviewWithExtraInfo[];
        groupedSymbolsByFundType?: InsightGetSymbolsPerTypeResponse;
    } = {},
): PortfolioWithExtraInfo {
    return {
        ...portfolio,
        entries: formatPortfolioPositions(
            portfolio.entries.map((entry) => {
                const tiprankSymbolData = tipranks?.find(
                    (data) => data.ticker === entry.symbol || data.ticker === entry.quote.underlyingSymbol,
                );
                return {
                    ...entry,
                    tipranks: tiprankSymbolData,
                };
            }),
            accounts,
            inventoryAccounts,
            balance,
            symbol,
            positionType,
            groupedSymbolsByFundType,
        ),
    };
}

export function formatMarketCapBreakdownChartData(positions: PositionWithExtraInfo[]): MarketCapBreakdownStat {
    const marketCapBreakdown = positions.reduce<Record<string, number>>((result, position) => {
        const key = position.marketCapClassification;
        if (result[key]) {
            result[key] += Math.abs(position.value); // positive value for short positions
        } else {
            result[key] = Math.abs(position.value);
        }

        return result;
    }, {});

    const total = sum(...Object.values(marketCapBreakdown));
    const labels = Object.keys(marketCapBreakdown).sort();
    const data = labels.map((key) => divide(marketCapBreakdown[key]!, total));

    return labels.map((label, index) => ({
        label,
        value: data[index]!,
    }));
}

export function formatOverExposedAccounts(
    data: OverExposedEntry[],
    overExposedType: OverExposedType,
    accounts: Account[],
): OverExposedAccount[] {
    const result = compact(
        data.map(({ accountId, concentration, quote, industryByType }) => {
            const account = accounts.find((account) => account.accountId === accountId);

            if (!account) {
                return null;
            }

            const item: OverExposedAccount = {
                overExposedType,
                accountId,
                accountNumber: account.accountNumber || '',
                fullName: account.fullName || '',
                exposure: concentration,
            };

            if (overExposedType === OverExposedType.SecurityType) {
                item.positionType = quote.securityType;
            }

            if (overExposedType === OverExposedType.Security) {
                item.symbol = quote.symbol;
            }

            if (overExposedType === OverExposedType.Industry) {
                item.sector = industryByType?.industryName;
            }

            return item;
        }),
    );

    return uniqWith(result, isEqual);
}

export function formatCorporateActions(
    data: CorporateAction[],
    positions: PositionWithExtraInfo[],
    actionTypes?: CorporateActionsType[],
): CorporateActionWithExtraInfo[] {
    const filteredData = data.filter((action) => {
        let match = true;

        if (actionTypes) {
            match = actionTypes.includes(action.type);
        }

        return match;
    });

    return filteredData.map((action) => {
        const position = positions.filter((position) => position.symbol === action.newEntry.symbol)[0];

        return {
            ...action,
            symbol: action.newEntry.symbol || '',
            companyName: action.newEntry.companyName || '',
            quantity: position?.quantity || 0,
            exposure:
                divide(
                    position?.costValue || 0,
                    sumBy(positions, (position) => position.costValue),
                ) || 0,
            dividendAmountPercentage: position?.dividendYield || 0,
            totalPayment: multiply(position?.quantity || 0, action.dividendAmount) || 0,
        };
    });
}

export function formatDriftReport(
    model: ModelWithExtraInfo,
    positions: Position[],
    combinedPositions: Position[],
): ModelDriftReportItem[];
export function formatDriftReport(
    account: AccountWithExtraInfo,
    positions: Position[],
    model?: ModelWithExtraInfo,
): AccountDriftReportItem[];
export function formatDriftReport(
    modelOrAccount: ModelWithExtraInfo | AccountWithExtraInfo,
    positions: Position[],
    modelOrCombinedPositions?: ModelWithExtraInfo | Position[],
): ModelDriftReportItem[] | AccountDriftReportItem[] {
    const seenPositions: Record<string, boolean> = {};

    const forModel = isArray(modelOrCombinedPositions);
    const model = forModel
        ? (modelOrAccount as ModelWithExtraInfo)
        : (modelOrCombinedPositions as ModelWithExtraInfo | undefined);
    const account = forModel ? null : (modelOrAccount as AccountWithExtraInfo);
    const driftReportPositions = forModel ? (modelOrCombinedPositions as Position[]) : positions;
    // we can potentially have positions with different types, that's why we are combining their value
    // we will display only one security in a model, regardless it's type
    const combinedPositionsValue = driftReportPositions.reduce<Record<string, number>>((result, position) => {
        const symbol = position.symbol;
        if (result.hasOwnProperty(symbol)) {
            result[symbol]! += position.value;
        } else {
            result[symbol] = position.value;
        }

        return result;
    }, {});

    const result: DriftReportItem[] = uniqBy(
        driftReportPositions.map((position) => {
            const component = model?.components.find((component) => component.symbol === position.symbol);
            const value = combinedPositionsValue[position.symbol] || 0;
            const actual = divide(value, forModel ? model?.balance || 0 : account?.balance || 0);
            const target = component?.percentage || 0;
            const difference = subtract(target, actual);
            const quote = formatQuote(position.quote);

            seenPositions[position.symbol] = true;

            return {
                symbol: position.symbol,
                quantity: position.quantity,
                companyName: quote.companyName,
                lastPrice: quote.lastPrice,
                actual,
                target,
                difference,
                option: position.option,
                short: position.short,
                mutualFund: quote.mutualFund,
                type: position.type,
                value,
                missing: !component,
                quote,
                bond: position.quote.bond,
            };
        }),
        (driftReportItem) => driftReportItem.symbol,
    );

    ((forModel ? modelOrAccount : modelOrCombinedPositions) as ModelWithExtraInfo | undefined)?.components.forEach(
        (component) => {
            if (!seenPositions.hasOwnProperty(component.symbol)) {
                const quote = formatQuote(component.quote);
                const position = positions.find((position) => position.symbol === component.symbol);

                result.push({
                    symbol: component.symbol,
                    quantity: 0,
                    companyName: quote.companyName,
                    lastPrice: quote.lastPrice || 0,
                    actual: 0,
                    target: component.percentage,
                    difference: -component.percentage,
                    option: component.option || false,
                    missing: true,
                    value: position?.value || 0,
                    short: position?.short || false,
                    type: position?.type || PositionType.Cash,
                    mutualFund: quote.mutualFund || false,
                    quote,
                    bond: quote.bond || false,
                });
            }
        },
    );

    result.forEach((item) => {
        if (forModel) {
            (item as ModelDriftReportItem).accountIds =
                model?.accounts
                    .filter((account) =>
                        positions.some(
                            (position) => position.accountId === account.accountId && position.symbol === item.symbol,
                        ),
                    )
                    .map((account) => account.accountId) || [];
        } else {
            (item as AccountDriftReportItem).accountType = account?.accountType || AccountType.Cash;
            (item as AccountDriftReportItem).accountBalance = account?.balance || 0;
        }
    });

    if (forModel) {
        return result as ModelDriftReportItem[];
    } else {
        return result as AccountDriftReportItem[];
    }
}

export function formatHoldingInfo(
    symbol: string,
    allPositions: Position[],
    balance: number,
    modelTarget: number,
): HoldingInfo {
    const holdingPositions = allPositions.filter((position) => position.symbol === symbol);
    const currency = holdingPositions[0]?.fx?.baseCurrency || holdingPositions[0]?.quote.currency;
    const isMutualFund = holdingPositions[0]?.quote.mutualFund ?? false;
    const totalValue = sumBy(holdingPositions, (position) =>
        isMutualFund
            ? multiply(position.quantity, multiply(position.price, divide(position.gainPercent, 100)))
            : position.value,
    );
    const totalCostValue = sumBy(holdingPositions, (position) =>
        isMutualFund ? multiply(position.quantity, position.price) : position.costValue,
    );
    const gainPercent = sumBy(holdingPositions, (position) => position.gainPercent);

    return {
        totalValue,
        totalCostValue,
        gainPercent,
        currency,
        price: holdingPositions[0]?.price || 0,
        totalHolding: sumBy(holdingPositions, (position) => position.quantity),
        positionWeight: divide(totalValue, balance),
        modelTarget,
    };
}

export function formatPositionAccounts(
    symbol: string,
    positionAccounts: PositionAccount[],
    branchPortfolio: PortfolioWithExtraInfo,
): PositionAccountWithExtraInfo[] {
    return uniqBy(positionAccounts, (position) => position.accountId).map((positionAccount) => {
        const positions = branchPortfolio.entries.filter(
            (position) => position.accountId === positionAccount.accountId && position.symbol === symbol,
        );
        const positionValue = sumBy(positions, (position) => position.value);

        return {
            ...positionAccount,
            accountNumber: positions[0]?.accountNumber || '',
            fullName: positions[0]?.fullName || '',
            positionValue,
            positionWeight: divide(positionValue, branchPortfolio.total),
            inventory: positions[0]?.inventory || false,
        };
    });
}

export function getEtfToCompare(symbol: string) {
    if (symbol === Etf.IVV || symbol === Etf.SPY || symbol === Etf.VOO) {
        return Etf.DIA;
    }

    return Etf.SPY;
}

export function formatAccountHistory(data: AccountHistory[]): AccountHistoryWithExtraInfo[] {
    return (
        // Removing all pending dividend history items and account type change
        data
            .filter(
                (item) =>
                    !(
                        (
                            item.status === AccountHistoryStatus.Pending ||
                            item.type === ActivityType.AccountTypeChange ||
                            item.type === ActivityType.AccountBalanceAdjustment
                        ) // filtered out on webtrader
                    ),
            )
            .map((item) => {
                const activities = Object.keys(item)
                    .filter((key) => key !== 'addition' && get(item, key) === true)
                    .map(titleCase);
                return {
                    ...item,
                    symbol:
                        item.type === ActivityType.BackofficeCashUpdate || item.type === ActivityType.Transfer
                            ? ''
                            : item.symbol,
                    description: item.details
                        ? item.details
                        : item.cashSubType === HistoryCashSubType.MarginInterest
                          ? 'MARGIN' + ': ' + item.cashType + ': ' + item.description
                          : item.description,
                    activity: activities.length > 0 ? activities.join(', ') : 'Other',
                };
            })
    );
}

export function formatOptionChain(quotes: Quote[]): OptionChain {
    return quotes.reduce<OptionChain>((result, quote) => {
        // root symbols can be AMC, AMC2, 1AMC2 after corp actions, etc
        // underlying will always be AMC
        // for option indexes (I:SPX) we need to target underlying symbol
        // for assets like BRK.B, BRK.A we need to target underlying symbol
        const rootSymbol =
            quote.underlyingSymbol?.includes(':') || quote.underlyingSymbol?.includes('.')
                ? quote.underlyingSymbol
                : quote.rootSymbol;

        const strikePrice = quote.strikePrice;

        if (!result.hasOwnProperty(rootSymbol)) {
            result[rootSymbol] = {};
        }

        if (!result[rootSymbol]!.hasOwnProperty(strikePrice)) {
            result[rootSymbol]![strikePrice] = {};
        }

        if (quote.call) {
            result[rootSymbol]![strikePrice]!.call = quote;
        } else {
            result[rootSymbol]![strikePrice]!.put = quote;
        }

        return result;
    }, {});
}

export const formatEtfBySector = (
    data: SectorBreakDown,
    industryGroupCode: GlobalGroupCode,
): EtfBreakdownWithExtraInfo[] => {
    return data[getSectorByIndustryCode(industryGroupCode)].map((item) => {
        return {
            ...item,
            symbol: item.symbol || '',
            companyName: item.quote.companyName || '',
            lastPrice: item.quote.lastPrice || 0,
            changePercent: item.quote.changePercent || 0,
        };
    });
};

export const getSectorIndustryByIndustryGroupCode = (data: SectorRanking[], industryGroupCode: GlobalGroupCode) =>
    data.filter((item) => String(item.industry.levelCode).startsWith(String(industryGroupCode)));

export const formatIpo = (data: InsightIPOResponse): IpoWithExtraInfo => ({
    ...data,
    // This is not guaranteed
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    change: data.quote?.change || 0,
});

export const formatUpcomingEarnings = (data: InsightEarningRelease) => {
    const quote = data.quote;
    const { comp_name = '', volume = 0, change = 0, change_percent = 0, price_change = 0, mkt_cap = 0 } = quote || {};
    return {
        ...data,
        companyName: comp_name,
        volume,
        change,
        changePercent: change_percent,
        price: price_change,
        marketCap: mkt_cap,
    };
};

export const formatEtfInitCommonParams = (data: EtfInit): EtfInitCommonParams => {
    const sponsors: EtfFilterOption[] = Object.values(data.Sponsors).map((item, index) => ({
        value: item.name,
        id: Object.keys(data.Sponsors)[index],
    }));
    const categories: EtfFilterOption[] = Object.values(data.Categories).map((item, index) => ({
        value: item,
        id: Object.keys(data.Categories)[index],
    }));
    const assets: EtfFilterOption[] = Object.values(data.AssetClasses).map((item, index) => ({
        value: item,
        id: Object.keys(data.AssetClasses)[index],
    }));
    const leverage: EtfFilterOption[] = Object.values(data.LeverageTypes).map((item, index) => ({
        value: item,
        id: Object.keys(data.LeverageTypes)[index],
        name: getLeverageType(item),
    }));
    const inverse: EtfFilterOption[] = Object.values(data.InverseTypes).map((item) => ({
        value: item,
        name: getInverseType(item),
    }));

    return {
        [EtfCommonParam.Sponsors]: sortBy(sponsors, (sponsor) => sponsor.value),
        [EtfCommonParam.Categories]: sortBy(categories, (category) => category.value),
        [EtfCommonParam.AssetClasses]: sortBy(assets, (asset) => asset.value),
        [EtfCommonParam.LeverageTypes]: sortBy(leverage, (asset) => asset.value),
        [EtfCommonParam.InverseTypes]: sortBy(inverse, (asset) => asset.value),
    };
};

export const formatDocumentData = (data?: DocumentItem[]): DocumentItemWithExtraInfo[] => {
    return (
        data?.map((item) => {
            const documentDate = item.documentDate || item.processDate || '';
            const documentType = item.documentType
                ? item.documentType
                : item.confirmUrl
                  ? 'Transaction Confirmation'
                  : '';

            return {
                ...item,
                documentType,
                documentDateFormatted: fromZonedTime(new Date(documentDate), MARKET_TIMEZONE),
                documentTypeFormatted: documentType,
                customType: `${item.accountNumber ? `${item.accountNumber}_` : ''}${item.documentType || 'Transaction Confirmation'}_${item.documentDate || item.processDate}`,
                documentDownload: item.url || item.confirmUrl || '',
            };
        }) || []
    );
};

export const formatLegalDocumentData = (data: LegalDocument[]): LegalDocumentWithExtraInfo[] => {
    return data.map((item: LegalDocument) => {
        return {
            name: item.revision.split('|')[0] || '',
            ...item,
        };
    });
};

export function formatQuoteRecommendation(
    data: InsightLatestAnalystRatingsOnStockResponse[] = [],
    symbol: string,
): RecommendationStat {
    const priceTargets = data.map((recommendation) => recommendation.price_target).filter((price) => price !== 0);

    return {
        symbol,
        recommendations: data,
        stats: {
            high: Math.max(...priceTargets),
            average: divide(sum(...priceTargets), priceTargets.length - 1),
            low: Math.min(...priceTargets),
        },
    };
}

export const formatQuoteFundamentals = (fundamentals: InsightGetSymbolFundamentalsAnnualResponse) => {
    const groupedData: QuoteFundamentals = {
        [Fundamentals.BalanceSheet]: [],
        [Fundamentals.CashFlow]: [],
        [Fundamentals.IncomeStatement]: [],
    };

    Object.entries(fundamentals).forEach(([dataType, data]) => {
        const categories = Object.keys(Object.values(data)[0] || {});

        const categoryDataArray = categories.map((category) => {
            const categoryData: QuoteFundamentalsCategory = { category };

            Object.entries(data).reduce((acc, [year, values]) => {
                acc[year] = get(values, category);
                return acc;
            }, categoryData);

            return categoryData;
        });

        groupedData[dataType as keyof QuoteFundamentalsData].push(...categoryDataArray);
    });

    return groupedData;
};
